import {
  ACESFilmicToneMapping,
  AxesHelper,
  Color,
  DirectionalLight,
  PerspectiveCamera,
  PMREMGenerator,
  Scene,
  Sphere,
  Vector3,
  WebGLRenderer,
  Fog,
  CylinderGeometry,
  MeshStandardMaterial,
  Mesh,
} from 'three';
import debounce from 'lodash/debounce';
import TWEEN from '@tweenjs/tween.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { createPointerEventListener, createPointerHitListener } from '../Utils/pointerEvents';
import { BACK, FRONT, FRONT_LEFT, FRONT_RIGHT, LEFT, RIGHT, TOP } from '../Constants/CameraViews';
import Observable from '../Utils/Observable';
import { getContainerBox } from './utils/objectSize';
import { findClosestEditable, getIntersection } from './utils/getIntersection';
import { InteractionStarted, ItemSelected, PointerIntersection, VisualizationUpdated } from './EventTypes';
import warehouseBuilder from './BuiderFunctions/warehouseBuilder';
import tentBuilder from './BuiderFunctions/tentBuilder';
import { fitSceneToCamera, setupDefaultControls, setupFixedViews, setupPanControls } from './cameraControls';
import makeScreenshots from './makeScreenshots';
import { globalLoaderStateManager } from '../App/GlobalLoader';
import isDevMode from '../Utils/isDevMode';
import disposeObject from './utils/disposeObject';
import { EventActions } from '../App/BuildingFactory/buildingFactory';
import { createEnvironmentLoader } from './BuiderFunctions/environmentLoader';

function getBuilder(itemIdentifier) {
  return (
    {
      warehouse: warehouseBuilder,
      tent: tentBuilder,
    }[itemIdentifier] || warehouseBuilder
  );
}

const highlightColor = new Color(0x385dad);

function createDefaultRenderer() {
  const renderer = new WebGLRenderer({
    antialias: true,
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  return renderer;
}

function createDefaultCamera() {
  const camera = new PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.01);
  camera.position.set(0.5, 1, 1).multiplyScalar(30);
  camera.lookAt(0, 0, 0);
  return camera;
}

function setLightShadow(light, d = 50) {
  // directional light has orthographic camera (due to the parallel ray direction)
  if (light.isDirectionalLight) {
    light.castShadow = true;
    light.shadow.mapSize.width = 1024;
    light.shadow.mapSize.height = 1024;
    light.shadow.camera.top = d;
    light.shadow.camera.bottom = -d;
    light.shadow.camera.left = -d;
    light.shadow.camera.right = d;
    light.shadow.camera.near = 0.0001;
    light.shadow.camera.far = 10000;
    light.shadow.camera.updateProjectionMatrix();
  }
}

// eslint-disable-next-line no-unused-vars
function createDirLights() {
  const dirLight = new DirectionalLight(0xffffff);
  dirLight.position.set(3, 10, 10);
  return [dirLight];
}

// eslint-disable-next-line no-unused-vars
function setShadows(root, dirLight) {
  root.traverse((object) => {
    if (object.isMesh) {
      object.castShadow = true;
    }
  });
  if (dirLight) {
    const box = getContainerBox(root);
    const sphere = new Sphere();
    box.getBoundingSphere(sphere);
    setLightShadow(dirLight, sphere.radius);
  }
}

function createScene() {
  const scene = new Scene();
  scene.background = new Color(0xffffff);
  scene.fog = new Fog(0xffffff, 150, 480);
  scene.add(...createDirLights());
  return scene;
}

function setTemporaryColor(object, color) {
  if (object.userData?.noHighlight) {
    return;
  }
  if (object.material) {
    if (!object.originalColor) {
      object.originalColor = object.material.color;
    }
    object.material.color = color;
  }
  for (const child of object.children) {
    setTemporaryColor(child, color);
  }
}

function restoreOriginalColor(object) {
  if (object.material) {
    object.material.color = object.originalColor;
  }
  for (const child of object.children) {
    restoreOriginalColor(child);
  }
}

function loadEnvMap(renderer, url) {
  return new Promise((resolve, reject) => {
    new RGBELoader().setPath('3d/textures/equirectangular/').load(
      url,
      function (texture) {
        const pmremGenerator = new PMREMGenerator(renderer);
        pmremGenerator.compileEquirectangularShader();

        const envMap = pmremGenerator.fromEquirectangular(texture).texture;

        texture.dispose();
        pmremGenerator.dispose();
        resolve(envMap);
      },
      undefined,
      reject
    );
  });
}

function getObjectBy(root, fn) {
  if (fn(root)) {
    return root;
  }
  for (const child of root.children) {
    const result = getObjectBy(child, fn);
    if (result) {
      return result;
    }
  }
}

function fixEnvironmentPosition(environment) {
  const floor = getObjectBy(environment, (obj) => obj.name?.startsWith('floor_center'));
  if (!floor) {
    return;
  }
  const containerBox = getContainerBox(floor);
  const containerCenter = containerBox.getCenter(new Vector3());
  environment.position.y = -containerBox.max.y;
  environment.position.x = -containerCenter.x;
}

function createFrontMarker() {
  const geometry = new CylinderGeometry(0.5, 0, 2, 32);
  geometry.computeVertexNormals();
  const material = new MeshStandardMaterial({ color: 0xffff00 });
  const cylinder = new Mesh(geometry, material);
  cylinder.position.set(20, 1, 0);
  return cylinder;
}

export function createVisualization(buildingConfiguration, options = {}) {
  const observable = new Observable();
  const { on, off, dispatch } = observable;

  const { createRenderer = createDefaultRenderer } = options;

  const renderer = createRenderer();
  const camera = createDefaultCamera();
  const scene = createScene();

  let isAnimating = false;
  let animationFrame = null;

  function updateCamera(width, height) {
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  function setSize({ width, height }) {
    renderer.setSize(width, height);
    updateCamera(width, height);
    renderFrame();
  }

  function updateRenderOrder() {
    if (!building?.walls) {
      return;
    }
    Object.values(building.walls)
      .sort((a, b) => a.position.distanceTo(camera.position) - b.position.distanceTo(camera.position))
      .reverse()
      .forEach((wall, wallOrder) => {
        if (wall.wallOrder === wallOrder) {
          return;
        }
        wall.wallOrder = wallOrder;
        wall.traverse((object) => {
          if (object.renderOrder) {
            if (!object.renderOrderOrig) {
              object.renderOrderOrig = object.renderOrder;
            }
            object.renderOrder = wallOrder * 1000 + object.renderOrderOrig;
          }
        });
      });
  }

  function render() {
    updateRenderOrder();
    renderer.render(scene, camera);
  }

  function compile() {
    renderer.compile(scene, camera);
  }

  function renderFrame() {
    if (!isAnimating) {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(render);
    }
  }

  let cameraMovementStarted = false;
  const controls = setupDefaultControls(camera, renderer.domElement, {
    change: () => {
      if (cameraMovementStarted) {
        itemHighlightListener.setDisabled(true);
        itemClickListener.setDisabled(true);
        interactionListener.setDisabled(true);
      }
      renderFrame();
    },
    start: () => {
      cameraMovementStarted = true;
    },
    end: () => {
      cameraMovementStarted = false;
      itemHighlightListener.setDisabled(false);
      itemClickListener.setDisabled(false);
      interactionListener.setDisabled(false);
    },
  });
  setupPanControls(controls, renderer.domElement, scene);
  setupFixedViews(controls, scene, { startAnimation, stopAnimation, render });

  function animate(time) {
    animationFrame = window.requestAnimationFrame(animate);
    if (time) {
      TWEEN.update();
      controls && controls.update();
      render();
    }
  }

  function startAnimation() {
    isAnimating = true;
    animate();
  }

  function stopAnimation() {
    window.cancelAnimationFrame(animationFrame);
    TWEEN.removeAll();
    isAnimating = false;
  }

  let lastHoveredObject;
  const itemHighlightListener = createPointerEventListener(renderer.domElement, 'move', (evt) => {
    if (evt.target !== renderer.domElement || !controls.enabled) {
      return;
    }
    const intersect = getIntersection(evt, renderer, camera, scene);
    const object = intersect && findClosestEditable(intersect.object);
    if (object !== lastHoveredObject) {
      if (lastHoveredObject) {
        restoreOriginalColor(lastHoveredObject);
      }
      if (object) {
        highlightColor.set(buildingConfiguration.getData().highlightColor);
        setTemporaryColor(object, highlightColor);
        lastHoveredObject = object;
      } else {
        lastHoveredObject = null;
      }
      renderFrame();
    }
  });

  const itemClickListener = createPointerHitListener(renderer.domElement, (evt) => {
    if (dispatch(PointerIntersection, { event: evt }).some((res) => res === false)) {
      isDevMode() && console.log('DEFAULT SELECT PREVENTED');
      return;
    }

    const intersect = getIntersection(evt, renderer, camera, scene);
    isDevMode() && console.log('OBJECT HIT', intersect && intersect.object);

    const object = intersect && findClosestEditable(intersect.object);
    object && dispatch(ItemSelected, { object, event: evt });
  });

  const interactionListener = createPointerEventListener(renderer.domElement, 'start', (evt) => {
    dispatch(InteractionStarted, { event: evt });
  });

  const environmentLoader = createEnvironmentLoader({
    cleanUpBeforeLoad(environment) {
      scene.remove(environment);
      disposeObject(environment);
      compile();
    },
  });

  let building, environment;
  const init = globalLoaderStateManager.wrap(async function init() {
    const envMapPromise = loadEnvMap(renderer, 'driving_school_1024x512.hdr');
    const buildingEnvMap = await loadEnvMap(renderer, 'driving_school_1024x512_building.hdr');

    const loader = getBuilder(buildingConfiguration.getData().itemIdentifier).createLoader({
      envMap: buildingEnvMap,
    });
    await Promise.all([loader.loadAll()]);

    updateVisualization(buildingConfiguration.getData());

    if (isDevMode()) {
      scene.add(createFrontMarker());

      const axesHelper = new AxesHelper(5);
      axesHelper.position.set(-25, 1, -25);
      scene.add(axesHelper);
    }

    const envMap = await envMapPromise;
    if (envMap) {
      scene.environment = envMap;
    }
  });

  const initializedPromise = init();
  let firstWarehouseRendered = false;

  const updateVisualization = globalLoaderStateManager.wrap(async function updateVisualization(
    buildingConfiguration
  ) {
    const oldEnvironment = environment;
    environment = await environmentLoader.load(buildingConfiguration);
    if (environment && oldEnvironment !== environment) {
      scene.add(environment);
      fixEnvironmentPosition(environment);
    }

    scene.remove(building);
    const { createBuilding } = getBuilder(buildingConfiguration.itemIdentifier);
    building = createBuilding(buildingConfiguration);
    scene.add(building);

    dispatch(VisualizationUpdated, { buildingConfiguration });

    compile();
    render();

    if (!firstWarehouseRendered) {
      firstWarehouseRendered = true;
      controls.fixedViews.moveTo(FRONT_LEFT, true);
    }

    fitSceneToCamera(building, controls, { startAnimation, stopAnimation });
  });

  const updateVisualizationDebounced = debounce(updateVisualization, 50);

  buildingConfiguration.onChange(async ({ data: { state, action } }) => {
    await initializedPromise;
    if (action === EventActions.cloneObject) {
      // in case of cloning direct update needed
      return updateVisualization(state);
    } else {
      updateVisualizationDebounced(state);
    }
  });

  function getScreenshots() {
    const { measurements } = visualization;
    const measurementsActive = measurements && measurements.isActive();

    const screenshots = makeScreenshots(visualization, [
      {
        name: FRONT_RIGHT,
        aspectRatio: 4 / 3,
        notes: true,
      },
      {
        name: FRONT_LEFT,
        aspectRatio: 4 / 3,
        notes: true,
      },
      { name: LEFT, aspectRatio: 16 / 9, measurements: true },
      { name: RIGHT, aspectRatio: 16 / 9, measurements: true },
      { name: FRONT, aspectRatio: 16 / 9, measurements: true },
      { name: BACK, aspectRatio: 16 / 9, measurements: true },
      { name: TOP, aspectRatio: 16 / 9 },
    ]);

    measurements && measurements.toggle(measurementsActive);

    return screenshots;
  }

  const visualization = {
    renderer,
    camera,
    scene,
    controls,
    compile,
    render,
    renderFrame,
    setSize,
    startAnimation,
    on,
    off,
    observable,
    getScreenshots,
  };

  return visualization;
}
